// =====================================================================
//  ADMIN CONSOLE, APPROVALS
// ---------------------------------------------------------------------
//  The workflow queue: everything routed through the approval engine,
//  with its step progression and full decision trail.
//
//  How this differs from the Overview page's approval list, which is a
//  flat "needs attention" roll-up read straight off the domain tables:
//  this page is the engine's own view. It can show multi-level chains
//  (manager, then HR), who decided each step and when, and requests that
//  are part-way through rather than only pending-or-done.
//
//  Two tabs, because they answer different questions:
//    My queue, only what this user can act on right now. Requests they
//                raised, and steps they are not an approver for, are
//                excluded server-side.
//    All, every request in the organization, any status, for
//                auditing and for chasing something up.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Inbox,
  ShieldCheck,
  UserMinus,
  X,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  Drawer,
  Field,
  KeyValue,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  StatusChip,
  Tabs,
  Textarea,
  Toolbar,
  ToolbarSpacer,
  FilterChip,
  EmptyState,
  Modal,
  toast,
} from '../ui';
import { fmtDateTime, orDash, timeAgo, titleCase } from '../format';
// Ink/surface variable names live in theme.js rather than being written
// inline, because the CSS names are shorter than the token names
// (`--ac-ink`, not `--ac-ink-strong`) and a wrong one fails silently.
import { cssVar } from '../theme';

// Icon and label per request type. Unknown types still render, the engine
// is designed to gain types without the console needing a release, so the
// fallback is deliberate rather than defensive.
const TYPE_META = {
  leave: { icon: CalendarDays, label: 'Leave' },
  user_deletion: { icon: UserMinus, label: 'Account removal' },
  access_request: { icon: ShieldCheck, label: 'Portal access' },
};

const typeMeta = (type) => TYPE_META[type] || { icon: Inbox, label: titleCase(type || 'Request') };

const STATUS_FILTER = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Step progression, rendered as a vertical trail inside the drawer. */
function StepTrail({ steps }) {
  if (!steps?.length) return null;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {steps.map((step) => {
        const isCurrent = step.state === 'current';
        const isDone = step.state === 'approved';
        const tone =
          step.state === 'approved'
            ? 'positive'
            : step.state === 'rejected'
              ? 'negative'
              : step.state === 'current'
                ? 'accent'
                : 'neutral';

        return (
          <div
            key={step.step_order}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: 1,
                display: 'grid',
                placeItems: 'center',
                background: isDone
                  ? 'var(--ac-pos-bg)'
                  : isCurrent
                    ? 'var(--ac-accent-bg)'
                    : cssVar.sunken,
                border: `1px solid ${
                  isDone ? 'var(--ac-pos-bd)' : isCurrent ? 'var(--ac-accent-bd)' : cssVar.line
                }`,
                color: isDone ? 'var(--ac-pos-fg)' : cssVar.inkMuted,
                fontSize: 11,
              }}
            >
              {isDone ? <Check size={12} /> : step.step_order}
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: cssVar.inkStrong }}>
                {step.label || `Step ${step.step_order}`}
              </div>
              <div style={{ fontSize: 12, color: cssVar.inkMuted }}>
                {step.approver_kind === 'role'
                  ? `Any ${(step.approver_roles || []).map(titleCase).join(' or ')}`
                  : step.approver_kind === 'reporting_manager'
                    ? "The requester's manager"
                    : 'A named approver'}
                {step.min_approvals > 1 ? ` · needs ${step.min_approvals} approvals` : ''}
              </div>
            </div>

            <ToolbarSpacer />
            <Chip tone={tone}>{titleCase(step.state)}</Chip>
          </div>
        );
      })}
    </div>
  );
}

/** Append-only record of who decided what. */
function DecisionTrail({ actions }) {
  if (!actions?.length) {
    return (
      <div style={{ fontSize: 13, color: cssVar.inkMuted }}>No decisions recorded yet.</div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {actions.map((action) => (
        <div key={action.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Chip tone={action.action === 'approved' ? 'positive' : 'negative'} dot>
            {titleCase(action.action)}
          </Chip>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, color: cssVar.inkStrong }}>
              {action.actor_name || 'System'}
              <span style={{ color: cssVar.inkFaint }}> · step {action.step_order}</span>
            </div>
            <div style={{ fontSize: 12, color: cssVar.inkMuted }}>
              {fmtDateTime(action.created_at)} · {timeAgo(action.created_at)}
            </div>
            {action.comment ? (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: cssVar.inkBase,
                  background: cssVar.sunken,
                  border: `1px solid ${cssVar.lineSoft}`,
                  borderRadius: 'var(--ac-r-sm)',
                  padding: '6px 8px',
                }}
              >
                {action.comment}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ApprovalsPage({ can }) {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('queue');
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [openId, setOpenId] = useState(null);

  // Rejecting without a reason is not useful to the person waiting on it,
  // so a rejection goes through a modal that asks for one.
  const [rejecting, setRejecting] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  // Whether this person can decide *a given request* depends on the policy
  // step it is sitting on, which only the server can resolve. This just
  // decides whether to render the controls at all; the API returns 403 if the
  // caller is not an approver for that particular step.
  const canDecide = can('approval.decide');

  const inbox = useQuery({
    queryKey: ['admin', 'approvals', 'inbox'],
    queryFn: () => backendApi.approvals.inbox(),
    refetchInterval: 60000,
  });

  const all = useQuery({
    queryKey: ['admin', 'approvals', 'all', status, type, page, pageSize],
    queryFn: () =>
      backendApi.approvals.page({
        status: status || undefined,
        type: type || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
    placeholderData: keepPreviousData,
    enabled: tab === 'all',
  });

  const detail = useQuery({
    queryKey: ['admin', 'approvals', 'detail', openId],
    queryFn: () => backendApi.approvals.get(openId),
    enabled: openId != null,
  });

  // One invalidation list for every mutation: the queue, the table, the
  // open drawer and the rail's badge counts all derive from this data.
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'approvals'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'approvalCounts'] });
  };

  const decide = useMutation({
    mutationFn: ({ id, decision, comment }) =>
      backendApi.approvals.decide(id, decision, comment),
    onSuccess: (result, variables) => {
      refresh();
      setRejecting(null);
      setRejectComment('');

      // Say what actually happened. "Approved" would be misleading on a
      // multi-level request that has only cleared its first step.
      if (variables.decision === 'reject') {
        toast.success('Request rejected.');
      } else if (result?.advanced) {
        toast.success('Approved, moved to the next approver.');
      } else if (result?.settled) {
        toast.success('Approved.');
      } else {
        toast.success('Your approval was recorded. This step needs another approver.');
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const queueRows = inbox.data || [];
  const rows = all.data?.rows || [];
  const total = all.data?.total || 0;

  const columns = useMemo(
    () => [
      {
        key: 'title',
        label: 'Request',
        render: (row) => {
          const meta = typeMeta(row.request_type);
          const Icon = meta.icon;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Icon size={14} style={{ color: cssVar.inkFaint, flexShrink: 0 }} />
              <PersonCell name={row.title} sub={row.summary} />
            </div>
          );
        },
        csv: (row) => row.title,
      },
      {
        key: 'request_type',
        label: 'Type',
        width: 150,
        render: (row) => <Chip>{typeMeta(row.request_type).label}</Chip>,
        csv: (row) => typeMeta(row.request_type).label,
      },
      {
        key: 'requested_by_name',
        label: 'Raised by',
        width: 170,
        render: (row) => orDash(row.requested_by_name),
      },
      {
        key: 'current_step',
        label: 'Progress',
        width: 110,
        align: 'right',
        sortable: false,
        // Only meaningful while a request is open; a settled one shows its
        // outcome in the status column instead.
        render: (row) =>
          row.status === 'pending' ? (
            <span className="ac-mono">
              {row.current_step} / {row.total_steps}
            </span>
          ) : (
            <span style={{ color: cssVar.inkFaint }}>,</span>
          ),
        csv: (row) => `${row.current_step}/${row.total_steps}`,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        render: (row) => <StatusChip value={row.status} />,
      },
      {
        key: 'created_at',
        label: 'Raised',
        width: 150,
        render: (row) => (
          <span title={fmtDateTime(row.created_at)}>{timeAgo(row.created_at)}</span>
        ),
        csv: (row) => row.created_at,
      },
    ],
    []
  );

  const decideActions = (row) =>
    canDecide && row.status === 'pending' ? (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button
          size="sm"
          variant="primary"
          icon={CheckCircle2}
          loading={decide.isPending && decide.variables?.id === row.id}
          onClick={(event) => {
            event.stopPropagation();
            decide.mutate({ id: row.id, decision: 'approve' });
          }}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="danger"
          icon={X}
          onClick={(event) => {
            event.stopPropagation();
            setRejecting(row);
          }}
        >
          Reject
        </Button>
      </div>
    ) : undefined;

  const record = detail.data;

  return (
    <>
      <PageHeader
        title="Approvals"
        sub="Multi-level workflows with a full decision trail"
      />

      <Tabs
        items={[
          { key: 'queue', label: 'My queue', count: queueRows.length, icon: Inbox },
          { key: 'all', label: 'All requests', icon: Clock },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'queue' ? (
        <Panel>
          <PanelHead
            title="Waiting on you"
            sub="Requests you are an approver for at their current step. Your own requests are excluded."
          />
          <PanelBody flush>
            <DataTable
              columns={columns.filter((c) => c.key !== 'status')}
              rows={queueRows}
              loading={inbox.isLoading}
              getRowId={(row) => row.id}
              onRowClick={(row) => setOpenId(row.id)}
              rowActions={decideActions}
              emptyIcon={CheckCircle2}
              emptyTitle="Nothing waiting on you"
              emptyText="When a request reaches a step you approve, it appears here."
            />
          </PanelBody>
        </Panel>
      ) : (
        <Panel>
          <PanelBody flush>
            <Toolbar>
              <FilterChip
                label="Status"
                value={status}
                options={STATUS_FILTER}
                onChange={(next) => {
                  setStatus(next);
                  setPage(0);
                }}
              />
              <FilterChip
                label="Type"
                value={type}
                options={Object.entries(TYPE_META).map(([value, meta]) => ({
                  value,
                  label: meta.label,
                }))}
                onChange={(next) => {
                  setType(next);
                  setPage(0);
                }}
              />
              <ToolbarSpacer />
            </Toolbar>

            <DataTable
              columns={columns}
              rows={rows}
              total={total}
              loading={all.isLoading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              getRowId={(row) => row.id}
              onRowClick={(row) => setOpenId(row.id)}
              rowActions={decideActions}
              emptyIcon={Inbox}
              emptyTitle={status ? `No ${status} requests` : 'No requests yet'}
              emptyText="Approval workflows appear here once employees start raising requests."
            />
          </PanelBody>
        </Panel>
      )}

      <Drawer
        open={openId != null}
        onClose={() => setOpenId(null)}
        title={record?.title || 'Approval request'}
        sub={record ? typeMeta(record.request_type).label : undefined}
        badge={record ? <StatusChip value={record.status} /> : null}
        footer={
          canDecide && record?.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                icon={CheckCircle2}
                loading={decide.isPending}
                onClick={() => decide.mutate({ id: record.id, decision: 'approve' })}
              >
                Approve
              </Button>
              <Button variant="danger" icon={X} onClick={() => setRejecting(record)}>
                Reject
              </Button>
            </div>
          ) : null
        }
      >
        {detail.isLoading ? (
          <EmptyState icon={Clock} title="Loading…" />
        ) : !record ? (
          <EmptyState icon={Inbox} title="Request not found" />
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            <KeyValue
              items={[
                { key: 'Raised by', value: orDash(record.requested_by_name) },
                { key: 'Raised', value: fmtDateTime(record.created_at) },
                {
                  key: 'Decided',
                  value: record.decided_at ? fmtDateTime(record.decided_at) : ',',
                },
                { key: 'Summary', value: orDash(record.summary) },
              ]}
            />

            {record.payload?.reason ? (
              <Notice tone="default" title="Reason given">
                {record.payload.reason}
              </Notice>
            ) : null}

            <div>
              <div
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: cssVar.inkFaint,
                  marginBottom: 8,
                }}
              >
                Approval chain
              </div>
              <StepTrail steps={record.steps} />
            </div>

            <div>
              <div
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: cssVar.inkFaint,
                  marginBottom: 8,
                }}
              >
                Decision trail
              </div>
              <DecisionTrail actions={record.actions} />
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        open={rejecting != null}
        onClose={() => {
          setRejecting(null);
          setRejectComment('');
        }}
        title="Reject request"
        sub={rejecting?.title}
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="danger"
              loading={decide.isPending}
              onClick={() =>
                decide.mutate({
                  id: rejecting.id,
                  decision: 'reject',
                  comment: rejectComment.trim() || undefined,
                })
              }
            >
              Reject request
            </Button>
            <Button
              onClick={() => {
                setRejecting(null);
                setRejectComment('');
              }}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <Notice tone="warning">
          Rejecting settles the whole request immediately, even if later approval
          steps remain. The person who raised it is notified.
        </Notice>
        <Field
          label="Reason"
          hint="Shown to the requester. Strongly recommended, so they know what to change."
        >
          <Textarea
            rows={4}
            value={rejectComment}
            onChange={(event) => setRejectComment(event.target.value)}
            placeholder="Explain why this is being rejected…"
          />
        </Field>
      </Modal>
    </>
  );
}
